/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, Text, Box } from 'theme-ui'
import { Fragment, useState, useEffect } from 'react'
import { useResponsiveValue } from '@theme-ui/match-media'
import { Transition } from 'react-transition-group'

import {
  HeroContainerStyled,
  HeroStyled,
  HeroBackgroundStyled,
  HeroContentStyled,
  HeroIconStyled,
  HeroCaptionStyled,
  HeroTitleStyled,
  HeroSubtitleStyled,
  HeroDescStyled,
  HeroCtaStyled,
  HeroCtaCaptionStyled,
  HeroMediaStyled,
  HeroBlockStyled,
  HeroAnimateContainerStyled,
  HeroAnimateImageBigStyled,
  HeroAnimateImageMediumStyled,
  HeroAnimateImageSmallStyled,
  HeroSvgStyled
} from './styles'
import { HeroProps } from './types'
import { transitionBigImageStyles, transitionImageStyles } from './cfg'
import Button from './../../atoms/Button'
import MediaItem from './../../molecules/MediaItem'
import { BgGradientBig, BgGradientSmall } from './../../../assets/svg/index'
import Image from './../../atoms/Image'
import StaticImage from './../../atoms/ImageStatic'

const Hero: React.FC<HeroProps> = ({ data, isHomepage, isBigMedia, isReversed, paddingTop, paddingBottom, smallGradient }) => {
  const isTablet = useResponsiveValue([false, true, true, true], {
    defaultIndex: 0
  })
  const isDesktop = useResponsiveValue([false, false, true, true], {
    defaultIndex: 0
  })

  const { withGradientBg, icon, title, isMainHeading, subtitle, desc, caption, cta, ctaSecondary, ctaCaption, media, animation } = data

  const [inViewAnimation, setInViewAnimation] = useState(false)

  useEffect(() => {
    setInViewAnimation(true)
  }, [])

  const backgroundImage = <Fragment>{isTablet ? <BgGradientBig /> : <BgGradientSmall />}</Fragment>

  return (
    <HeroContainerStyled
      {...{ isHomepage, isBigMedia, isReversed }}
      sx={{
        paddingTop: paddingTop ? paddingTop : ['90px', '', '109px'],
        paddingBottom: paddingBottom ? paddingBottom : ['48px', '', '95px']
      }}
    >
      <HeroStyled>
        {withGradientBg && <HeroBackgroundStyled {...{ smallGradient }}>{backgroundImage}</HeroBackgroundStyled>}
        <HeroBlockStyled variant="inners.primary">
          <HeroContentStyled>
            {icon && (
              <HeroIconStyled>
                <StaticImage image={icon} />
              </HeroIconStyled>
            )}

            {title && (
              <HeroTitleStyled>
                <Text
                  as={isMainHeading ? 'h1' : 'h2'}
                  sx={{
                    variant: isHomepage ? 'headingRaleway4' : 'headingRaleway6',
                    fontWeight: isHomepage ? ['bold', '', 'extrabold'] : 'bold',
                    color: isHomepage ? 'black' : 'green'
                  }}
                >
                  {title}
                </Text>
              </HeroTitleStyled>
            )}
            {subtitle && (
              <HeroSubtitleStyled>
                <Text
                  sx={{
                    variant: 'headingRaleway7',
                    fontFamily: 'secondary',
                    fontWeight: 'bold'
                  }}
                >
                  {subtitle}
                </Text>
              </HeroSubtitleStyled>
            )}
            {desc && (
              <HeroDescStyled
                sx={{
                  variant: 'headingMontserrat3'
                }}
                dangerouslySetInnerHTML={{ __html: desc }}
              />
            )}
            {caption && (
              <HeroCaptionStyled
                sx={{
                  variant: 'bodyMontserrat12',
                  fontFamily: 'primary'
                }}
                dangerouslySetInnerHTML={{ __html: caption }}
              />
            )}
            {cta || ctaSecondary ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  flexDirection: 'column'
                }}
              >
                <HeroCtaStyled>
                  {cta && (
                    <Button
                      variant="withArrow"
                      url={cta.url}
                      urlText={cta.urlText}
                      urlIsExternal={cta.urlIsExternal}
                      urlIsLogin={cta.urlIsLogin}
                      urlIsRegister={cta.urlIsRegister}
                      caption={cta.caption}
                      isCentered={cta.isCentered}
                    />
                  )}
                  {ctaSecondary && (
                    <Button
                      variant="withArrowAlt"
                      url={ctaSecondary.url}
                      urlText={ctaSecondary.urlText}
                      urlIsExternal={ctaSecondary.urlIsExternal}
                      urlIsLogin={ctaSecondary.urlIsLogin}
                      urlIsRegister={ctaSecondary.urlIsRegister}
                      urlIsDemo={ctaSecondary.urlIsDemo}
                      caption={ctaSecondary.caption}
                      isCentered={cta.isCentered}
                    />
                  )}
                </HeroCtaStyled>
              </Box>
            ) : null}
            {ctaCaption && <HeroCtaCaptionStyled>{ctaCaption}</HeroCtaCaptionStyled>}
          </HeroContentStyled>
          <HeroMediaStyled>
            {media.image || media.video ? (
              <MediaItem {...{ media }} />
            ) : animation ? (
              isDesktop && animation.desktop ? (
                <HeroAnimateContainerStyled>
                  {animation.desktop.imageBig && (
                    <Transition in={inViewAnimation} timeout={450} appear>
                      {(state) => (
                        <HeroAnimateImageBigStyled
                          style={{
                            ...transitionBigImageStyles[state]
                          }}
                        >
                          <Image fluid={animation.desktop.imageBig} alt={animation.desktop.imageBig.alt} />
                        </HeroAnimateImageBigStyled>
                      )}
                    </Transition>
                  )}
                  {animation.desktop.imageMedium && (
                    <Transition in={inViewAnimation} timeout={850} appear>
                      {(state) => (
                        <HeroAnimateImageMediumStyled
                          style={{
                            ...transitionImageStyles[state]
                          }}
                        >
                          <Image fluid={animation.desktop.imageMedium} alt={animation.desktop.imageMedium.alt} />
                        </HeroAnimateImageMediumStyled>
                      )}
                    </Transition>
                  )}
                  {animation.desktop.imageSmall && (
                    <Transition in={inViewAnimation} timeout={1150} appear>
                      {(state) => (
                        <HeroAnimateImageSmallStyled
                          style={{
                            ...transitionImageStyles[state]
                          }}
                        >
                          <Image fluid={animation.desktop.imageSmall} alt={animation.desktop.imageSmall.alt} />
                        </HeroAnimateImageSmallStyled>
                      )}
                    </Transition>
                  )}
                </HeroAnimateContainerStyled>
              ) : !isDesktop && animation.mobile ? (
                <HeroSvgStyled>
                  <StaticImage image={animation.mobile} />
                </HeroSvgStyled>
              ) : (
                ''
              )
            ) : (
              ''
            )}
          </HeroMediaStyled>
        </HeroBlockStyled>
      </HeroStyled>
    </HeroContainerStyled>
  )
}

export default Hero
